import _ from 'lodash'
import { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import PopupState, { bindMenu, bindTrigger } from 'material-ui-popup-state'
import { CirclePicker } from 'react-color'

import {
  Box,
  Checkbox,
  Divider,
  Drawer,
  FormControlLabel,
  FormGroup,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import MoreVertIcon from '@mui/icons-material/MoreVert'

import { MAIN_HEADER_HEIGHT } from '@/layouts/main'
import { MeetingLink } from '@/core/models/meeting-link.model'
import { OrderStatus } from '@/core/models/order.model'

import SelectInput from '@/components/ui/inputs/SelectInput'

const orderStatusColors: Record<OrderStatus, string> = {
  [OrderStatus.Pending]: '#ffee58',
  [OrderStatus.Accepted]: '#66bb6a',
  [OrderStatus.Confirmed]: '#66bb6a',
  [OrderStatus.Refused]: '#ef5350',
  [OrderStatus.Canceled]: '#9e9e9e',
  [OrderStatus.In_Progress]: '#42a5f5',
  [OrderStatus.Consultation_Created]: '#42a5f5',
  [OrderStatus.Completed]: '#ff9800',
  [OrderStatus.No_Show]: '#9e9e9e',
  [OrderStatus.Rescheduled]: '#ab47bc',
}

export type AgendaDrawerProps = {
  staffs: string[]
  staffScheduleColors: Record<string, string>
  staffAppointmentVisibilities: Record<string, boolean>
  setStaffs: Dispatch<SetStateAction<string[]>>
  setStaffScheduleColors: Dispatch<SetStateAction<Record<string, string>>>
  setStaffAppointmentVisibilities: Dispatch<SetStateAction<Record<string, boolean>>>
  staffOptions: Array<{ label: string; value: string }>
  orderStatusFilter: OrderStatus[]
  onOrderStatusFilterChange: (statuses: OrderStatus[]) => void
  meetingLinkFilter: string[]
  onMeetingLinkFilterChange: (meetingLinkIds: string[]) => void
  meetingLinks: MeetingLink[] | undefined
}

const paletteColors = [
  '#f44336',
  '#e91e63',
  '#9c27b0',
  '#673ab7',
  '#3f51b5',
  '#2196f3',
  '#03a9f4',
  '#00bcd4',
  '#009688',
  '#4caf50',
  '#8bc34a',
  '#cddc39',
  '#ffeb3b',
  '#ffc107',
  '#ff9800',
  '#ff5722',
  '#795548',
  '#607d8b',
]

export default function AgendaDrawer(props: AgendaDrawerProps) {
  const {
    staffs,
    staffScheduleColors,
    staffAppointmentVisibilities,
    setStaffs,
    setStaffScheduleColors,
    setStaffAppointmentVisibilities,
    staffOptions,
    orderStatusFilter,
    onOrderStatusFilterChange,
    meetingLinkFilter,
    onMeetingLinkFilterChange,
    meetingLinks,
  } = props
  const { t } = useTranslation()

  const handleMeetingLinkToggle = (meetingLinkId: string) => {
    if (meetingLinkFilter.includes(meetingLinkId)) {
      onMeetingLinkFilterChange(meetingLinkFilter.filter((id) => id !== meetingLinkId))
    } else {
      onMeetingLinkFilterChange([...meetingLinkFilter, meetingLinkId])
    }
  }

  const handleStatusToggle = (status: OrderStatus) => {
    if (orderStatusFilter.includes(status)) {
      onOrderStatusFilterChange(orderStatusFilter.filter((s) => s !== status))
    } else {
      onOrderStatusFilterChange([...orderStatusFilter, status])
    }
  }

  return (
    <Drawer
      variant="permanent"
      sx={{
        display: { xs: 'none', lg: 'block' },
        width: { xs: 300, xl: 360 },
        minHeight: 'inherit',
        height: `calc(100vh - ${MAIN_HEADER_HEIGHT}px)`,
        flexShrink: 0,
        [`& .MuiDrawer-paper`]: {
          width: { xs: 300, xl: 360 },
          boxSizing: 'border-box',
          position: 'absolute',
        },
      }}
    >
      <Toolbar sx={{ minHeight: 'unset !important', height: MAIN_HEADER_HEIGHT }} />
      <Stack sx={{ p: 2 }}>
        <SelectInput
          multiple
          onChange={(event) => {
            const {
              target: { value },
            } = event
            const newSelectedStaffs = typeof value === 'string' ? value.split(',') : value
            setStaffs(newSelectedStaffs)
            setStaffScheduleColors((prevScheduleColors) => {
              return _.reduce(
                newSelectedStaffs,
                (scheduleColors, staff) => {
                  scheduleColors[staff] = prevScheduleColors[staff] ?? paletteColors[_.random(0, paletteColors.length - 1)]
                  return scheduleColors
                },
                {} as typeof staffScheduleColors
              )
            })
            setStaffAppointmentVisibilities((prevVisibilities) => {
              return _.reduce(
                newSelectedStaffs,
                (visibilities, staff) => {
                  visibilities[staff] = prevVisibilities[staff] ?? true
                  return visibilities
                },
                {} as typeof staffAppointmentVisibilities
              )
            })
          }}
          options={staffOptions}
          placeholder="Search for people"
          value={staffs}
        />
        <List
          sx={{
            width: '100%',
            maxWidth: 360,
            bgcolor: 'background.paper',
          }}
        >
          {staffOptions
            .filter((option) => staffs.includes(option.value))
            .map((staffOption) => {
              return (
                <ListItem
                  key={staffOption.value}
                  secondaryAction={
                    <StaffMoreMenu
                      staffId={staffOption.value}
                      staffScheduleColors={staffScheduleColors}
                      setStaffs={setStaffs}
                      setStaffScheduleColors={setStaffScheduleColors}
                    />
                  }
                  disablePadding
                  sx={{
                    '&:hover .more-btn': {
                      visibility: 'visible',
                    },
                  }}
                >
                  <ListItemButton dense>
                    <ListItemIcon
                      sx={{
                        minWidth: 'unset',
                      }}
                    >
                      <Checkbox
                        edge="start"
                        checked={!!staffAppointmentVisibilities[staffOption.value]}
                        disableRipple
                        onChange={(_event, checked) => {
                          setStaffAppointmentVisibilities((prevVisibilities) => ({ ...prevVisibilities, [staffOption.value]: checked }))
                        }}
                        sx={[
                          !!staffScheduleColors[staffOption.value] && {
                            color: staffScheduleColors[staffOption.value],
                            '&.Mui-checked': {
                              color: staffScheduleColors[staffOption.value],
                            },
                          },
                        ]}
                      />
                    </ListItemIcon>
                    <ListItemText primary={staffOption.label} />
                  </ListItemButton>
                </ListItem>
              )
            })}
        </List>
        <Divider sx={{ my: 2 }} />
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
          {t('page.appointments.filter.statusLabel')}
        </Typography>
        <FormGroup>
          {Object.values(OrderStatus).map((status) => (
            <FormControlLabel
              key={status}
              control={
                <Checkbox
                  checked={orderStatusFilter.length === 0 || orderStatusFilter.includes(status)}
                  onChange={() => handleStatusToggle(status)}
                  size="small"
                  sx={{ color: orderStatusColors[status], '&.Mui-checked': { color: orderStatusColors[status] } }}
                />
              }
              label={<Typography variant="body2">{t(`page.appointments.status.${status}`)}</Typography>}
            />
          ))}
        </FormGroup>
        {meetingLinks && meetingLinks.length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              {t('page.appointments.filter.meetingLink')}
            </Typography>
            <FormGroup>
              {meetingLinks.map((ml) => (
                <FormControlLabel
                  key={ml._id}
                  control={
                    <Checkbox
                      checked={meetingLinkFilter.length === 0 || meetingLinkFilter.includes(ml._id)}
                      onChange={() => handleMeetingLinkToggle(ml._id)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">{ml.name}</Typography>}
                />
              ))}
            </FormGroup>
          </>
        )}
      </Stack>
    </Drawer>
  )
}

type StaffMoreMenuProps = {
  staffId: string
  staffScheduleColors: Record<string, string>
  setStaffs: Dispatch<SetStateAction<string[]>>
  setStaffScheduleColors: Dispatch<SetStateAction<Record<string, string>>>
}

function StaffMoreMenu({ staffId, staffScheduleColors, setStaffs, setStaffScheduleColors }: StaffMoreMenuProps) {
  const { t } = useTranslation()
  return (
    <PopupState variant="popover" popupId="groups-table-configuration">
      {(popupState) => (
        <>
          <IconButton {...bindTrigger(popupState)} className="more-btn" edge="end" sx={{ visibility: 'hidden' }}>
            <MoreVertIcon />
          </IconButton>
          <Menu {...bindMenu(popupState)} anchorOrigin={{ vertical: 'top', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
            <MenuItem
              onClick={() => {
                setStaffs((prevStaffs) => prevStaffs.filter((prevStaffId) => prevStaffId !== staffId))
                setStaffScheduleColors((prevStaffScheduleColors) => _.omit(prevStaffScheduleColors, staffId))
                popupState.close()
              }}
            >
              <ListItemIcon>
                <DeleteOutlineIcon />
              </ListItemIcon>
              {t('common:button.delete.label')}
            </MenuItem>
            <Divider />
            <Stack alignItems="center">
              <Box pl={2}>
                <CirclePicker
                  width="216px"
                  circleSize={20}
                  circleSpacing={16}
                  color={staffScheduleColors[staffId]}
                  colors={paletteColors}
                  onChange={(color) => {
                    setStaffScheduleColors((prevStaffScheduleColors) => ({ ...prevStaffScheduleColors, [staffId]: color.hex }))
                  }}
                  styles={{ default: { card: { margin: 0 } } }}
                />
              </Box>
            </Stack>
          </Menu>
        </>
      )}
    </PopupState>
  )
}
